import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate, Navigate, Link, useLocation } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import CookieConsent from "./components/CookieConsent";
import { LanguageProvider, useTranslation } from "./i18n/LanguageContext";
import LanguageSwitcher from "./i18n/LanguageSwitcher";
import { Components } from "./components";
import ModernLayout from "./ModernLayout.js";
import AdminLayout from "./AdminLayout.js";
import MembershipPage from "./MembershipPage";
import StaticPage from "./StaticPage";
import NotFoundPage from "./NotFoundPage";
import PaymentThankYou from "./components/PaymentThankYou";
import AccountBanned from "./AccountBanned";
import AccountSuspended from "./AccountSuspended";
import "./lockoutInterceptor";  // Installs global fetch interceptor for banned/suspended 403s
import HolidaysPage from "./HolidaysPage";
import MoroccoGame from "./MoroccoGame";
import MoroccoConversationGame from "./MoroccoConversationGame";
import MoroccoJourneyGame from "./MoroccoJourneyGame";
// WordPuzzleGame now rendered inside GamesSection (components.js)
import MoroccoJourneySimple from "./MoroccoJourneySimple";
import GameEditorDemo from "./GameEditorDemo";
import AdminGameEditor from "./components/admin/AdminGameEditor";
import Game from "./Game";
import Dictionary from "./Dictionary";
import AmazighTranslation from "./AmazighTranslation";
import TravelTools from "./TravelTools";
import LearningPage from "./LearningPage";
import DarijaDecoder from "./DarijaDecoder";
import VerbExplorer from "./VerbExplorer";
import DailyWordsPage from "./DailyWordsPage";
import DashboardPage from "./DashboardPage";
import ResetPassword from "./components/auth/ResetPassword";
import VerifyEmail from "./components/auth/VerifyEmail";
import SEO, { RouteSEO } from "./SEO";
import { 
  saveTranslationOffline, 
  getTranslationOffline, 
  getCacheStats 
} from "./offlineStorage";

// 404 Guard — redirects unknown paths that reach the catch-all route
const VALID_CATCHALL_PATHS = [
  '/', '/translation', '/decoder', '/learn', '/music', '/culture-pod',
  '/my-daily-words', '/verb-explorer', '/login', '/signup', '/thank-you',
  '/dictionary', '/amazigh-translation', '/travel-tools', '/my-morocco', '/MoroccanHolidays',
  '/holidays', '/dashboard', '/profile', '/membership', '/orders', '/games',
  '/help', '/help/documentation', '/help/documentation/deleteaccount',
];

// Game URL slug → autoOpenGame ID expected by the games tab.
const GAME_URL_TO_ID = {
  'WordChallenge': 'word-challenge',
  'LearningClock': 'learning-clock',
  'ConversationChallenge': 'conversation-challenge',
  'SoukBargainGame': 'souk-bargain-game',
  'SoukLa7wayj': 'souk-clothes-game',
  'SceneExplorer': 'scene-explorer',
  'WordPuzzle': 'word-puzzle',
  'LetterFall': 'letter-fall',
  'LetterEater': 'letter-eater',
};

// Maps a URL pathname to the (currentView, initialProfileTab) state pair.
// Used by the URL→state useEffect on mount and the popstate handler so that
// every URL keeps content + URL in sync without rewriting the address bar.
const pathnameToViewState = (pathname, search) => {
  // /games/<GameName>[/...] → opens the games tab. Nested segments (e.g.
  // /games/WordChallenge/arabic/level1) are consumed by the game component
  // itself; here we only need the top-level game slug.
  // /games/MoroccoJourney is excluded — that route renders the full game UI.
  if (pathname.startsWith('/games/') && !pathname.startsWith('/games/MoroccoJourney')) {
    const slug = pathname.split('/games/')[1].split('/')[0];
    const gameId = GAME_URL_TO_ID[slug];
    return { view: 'profile', tab: 'games', autoOpenGame: gameId };
  }
  // /profile?tab=<X> — read the query param synchronously so first render has the right tab
  // (otherwise UserProfile shows a brief profile-tab flash before useEffect switches it).
  if (pathname === '/profile' && search) {
    try {
      const tab = new URLSearchParams(search).get('tab');
      if (tab) return { view: 'profile', tab };
    } catch {}
  }
  const map = {
    '/translation':                     { view: 'translator' },
    '/dictionary':                      { view: 'dictionary' },
    '/decoder':                         { view: 'decoder' },
    '/learn':                           { view: 'learning' },
    '/music':                           { view: 'music' },
    '/culture-pod':                     { view: 'culturePod' },
    '/verb-explorer':                   { view: 'verbExplorer' },
    '/my-daily-words':                  { view: 'my-daily-words' },
    '/dashboard':                       { view: 'dashboard' },
    '/travel-tools':                      { view: 'traveltools' },
    '/my-morocco':                        { view: 'traveltools' },
    '/MoroccanHolidays':                { view: 'holidays' },
    '/holidays':                        { view: 'holidays' },
    '/amazigh-translation':             { view: 'amazigh-translation' },
    '/profile':                         { view: 'profile', tab: 'profile' },
    '/membership':                      { view: 'profile', tab: 'membership' },
    '/orders':                          { view: 'profile', tab: 'orders' },
    '/games':                           { view: 'profile', tab: 'games' },
    '/help':                            { view: 'profile', tab: 'help' },
    '/help/documentation':              { view: 'profile', tab: 'help' },
    '/help/documentation/deleteaccount':{ view: 'profile', tab: 'delete' },
    '/':                                { view: 'translator' },
  };
  return map[pathname] || null;
};
const CatchAllGuard = () => {
  useEffect(() => {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    // Game URLs are dynamic (/games/WordChallenge etc.), not enumerable
    if (path.startsWith('/games/')) return;
    if (!VALID_CATCHALL_PATHS.includes(path)) {
      window.location.replace('/404');
    }
  }, []);
  return null;
};


// SEO-Friendly Route Redirect Component
const ProfileRedirect = ({ setCurrentView, setInitialProfileTab, tab }) => {
  const navigate = useNavigate();

  useEffect(() => {
    // Allow URL ?tab=<name> to override the route's hardcoded tab.
    // Used by SSR /membership → /profile?tab=membership redirect for logged-in users.
    const urlTab = new URLSearchParams(window.location.search).get('tab');
    setCurrentView('profile');
    setInitialProfileTab(urlTab || tab);
    // Navigate to root to show the content
    navigate('/', { replace: true });
  }, [setCurrentView, setInitialProfileTab, tab, navigate]);
  
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );
};

// Delete Account Route - keeps URL as /deleteaccount (for Google Play Store requirement)
const DeleteAccountPage = ({ API, userToken, isDarkMode, currentUser, onLogout }) => {
  const { t } = useTranslation();
  const [reason, setReason] = React.useState('');
  const [details, setDetails] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!reason) {
      alert('Please select a reason for leaving');
      return;
    }
    setIsSubmitting(true);
    try {
      // Submit deletion request
      const response = await fetch(`${API}/user/delete-request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason, details })
      });
      if (response.ok) {
        setSubmitted(true);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to submit request. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting deletion request:', err);
      alert('An error occurred. Please try again.');
    }
    setIsSubmitting(false);
  };

  if (!currentUser) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center p-8">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('app_login_required')}</h1>
          <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('app_please_login_to_access')}</p>
          <button onClick={() => navigate('/')} className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            {t('app_go_to_login')}
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">✅</div>
          <h1 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Request Submitted</h1>
          <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Your account deletion request has been submitted. Your account will be deleted after 30 days. 
            You can cancel this request by logging in within this period.
          </p>
          <button onClick={() => navigate('/')} className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} py-12 px-4`}>
      <div className="max-w-lg mx-auto">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className={`flex items-center gap-2 mb-6 px-4 py-2 rounded-lg transition-colors ${
            isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-200'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <div className={`rounded-xl shadow-lg p-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <h1 className="text-2xl font-bold text-red-600 mb-6">⚠️ Delete Account</h1>
          
          <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 mb-6">
            <p className="text-red-700 dark:text-red-300 font-semibold mb-2">
              Warning: This action is permanent and cannot be undone!
            </p>
            <p className="text-sm text-red-600 dark:text-red-400">
              Your account will be scheduled for deletion after 30 days. You can cancel within this period.
            </p>
          </div>
          
          <div className="space-y-6">
            <div>
              <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Please tell us why you're leaving
              </label>
              <div className="space-y-2">
                {['Not using it anymore', 'Found a better alternative', 'Too expensive', 'Missing features', 'Privacy concerns', 'Other'].map(r => (
                  <label key={r} className={`flex items-center gap-2 cursor-pointer ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <input 
                      type="radio" 
                      name="delete_reason" 
                      value={r} 
                      checked={reason === r}
                      onChange={(e) => setReason(e.target.value)}
                      className="w-4 h-4"
                    />
                    {r}
                  </label>
                ))}
              </div>
            </div>
            
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Additional Details (Optional)
              </label>
              <textarea
                rows="4"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Tell us more about your decision..."
                className={`w-full px-4 py-2 border rounded-lg ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            
            <button 
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting...' : 'Request Account Deletion'}
            </button>
            
            <button 
              onClick={() => navigate('/')}
              className={`w-full px-4 py-3 rounded-lg font-semibold ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Holidays Route Redirect Component
const HolidaysRedirect = ({ setCurrentView }) => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Set the view to holidays
    setCurrentView('holidays');
    // Navigate to root to show the content
    navigate('/', { replace: true });
  }, [setCurrentView, navigate]);
  
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );
};

// Dictionary Route Redirect Component
const DictionaryRedirect = ({ setCurrentView }) => {
  const navigate = useNavigate();
  
  useEffect(() => {
    setCurrentView('dictionary');
    navigate('/', { replace: true });
  }, [setCurrentView, navigate]);
  
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );
};

// Amazigh Translation Route Redirect Component
const AmazighTranslationRedirect = ({ setCurrentView }) => {
  const navigate = useNavigate();

  useEffect(() => {
    setCurrentView('amazigh-translation');
    navigate('/', { replace: true });
  }, [setCurrentView, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
    </div>
  );
};

// Travel Tools Route Redirect Component (formerly My Morocco)
const TravelToolsRedirect = ({ setCurrentView }) => {
  const navigate = useNavigate();
  
  useEffect(() => {
    setCurrentView('traveltools');
    navigate('/', { replace: true });
  }, [setCurrentView, navigate]);
  
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );
};

// Decoder Route Redirect Component - SEO Friendly
const DecoderRedirect = ({ setCurrentView }) => {
  const navigate = useNavigate();

  useEffect(() => {
    setCurrentView('decoder');
    navigate('/', { replace: true });
  }, [setCurrentView, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
    </div>
  );
};

// Dashboard Route Redirect Component
const DashboardRedirect = ({ setCurrentView }) => {
  const navigate = useNavigate();
  useEffect(() => {
    setCurrentView('dashboard');
    navigate('/', { replace: true });
  }, [setCurrentView, navigate]);
  return null;
};

// Translation Route Redirect Component - SEO Friendly
const TranslationRedirect = ({ setCurrentView }) => {
  useEffect(() => {
    setCurrentView('translation');
  }, [setCurrentView]);
  
  return null; // Return nothing - let the main app render the content
};

// Game Route Redirect Component - Opens game in Games tab
const GameRedirect = ({ setCurrentView, setInitialProfileTab, gameId }) => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Special handling for Morocco Journey - redirect to new URL
    if (gameId === 'morocco-journey-redirect') {
      navigate('/games/MoroccoJourney', { replace: true });
      return;
    }
    // Other games are reached via /games/<Name> URLs handled by the catch-all.
    // GamesSection reads the URL directly. No localStorage middleman.
  }, [setCurrentView, setInitialProfileTab, gameId, navigate]);
  
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );
};



const {
  Header,
  TranslationInterface,
  AdminPanel,
  Navigation,
  AdminLogin,
  UserRegister,
  UserLogin,
  UserProfile
} = Components;

// Refreshes App.js userPlan after a successful Stripe payment so the sidebar
// "Pro" badge appears without requiring re-login. Triggered by ?payment=success
// (PaymentThankYou redirects to /profile?tab=membership&payment=success).
function PaymentSuccessRefresher({ API, userToken, setUserPlan }) {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('payment') !== 'success') return;
    const token = userToken || localStorage.getItem('user_token');
    if (!token) return;
    fetch(`${API}/user/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.plan) {
          setUserPlan(data.plan);
          try { localStorage.setItem('user_plan', JSON.stringify(data.plan)); } catch {}
        }
      })
      .catch(() => {});
  }, [location.search, API, userToken, setUserPlan]);
  return null;
}

function App() {
  const { t } = useTranslation();

  // Lazy-init currentView + initialProfileTab from URL synchronously using the
  // existing pathnameToViewState helper. Without this, every non-translation
  // page refresh briefly shows TranslationInterface (because the Redirect
  // components like DictionaryRedirect call setCurrentView from useEffect,
  // which runs AFTER first render). Mapping the URL on first render makes the
  // correct page render from frame 1. For game URLs, GamesSection reads the
  // URL directly to determine which game to open — no localStorage middleman.
  const _initialUrlState = (() => {
    try {
      const state = pathnameToViewState(window.location.pathname, window.location.search);
      if (state) return state;
    } catch {}
    return { view: 'translator' };
  })();
  const [currentView, setCurrentView] = useState(_initialUrlState.view || 'translator');
  const [initialProfileTab, setInitialProfileTab] = useState(_initialUrlState.tab || 'dashboard');
  // After SSR-landing -> /login?plan=X -> login completes, we open the
  // membership tab and highlight the chosen plan. Cleared after highlight runs.
  const [highlightPlanId, setHighlightPlanId] = useState(null);
  const [languages, setLanguages] = useState(() => {
    // Cache the language list synchronously so the language dropdown on the
    // translation page renders the user's flag + name from frame 1 instead
    // of flashing empty until /languages fetch resolves.
    try { const c = localStorage.getItem('languagesCache'); if (c) return JSON.parse(c); } catch {}
    return [];
  });
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    try { const c = localStorage.getItem('selectedLanguageCache'); if (c) return JSON.parse(c); } catch {}
    return "";
  });
  const [inputText, setInputText] = useState("");
  const [translationResult, setTranslationResult] = useState(null);
  const [genderFilter, setGenderFilter] = useState(""); // "" = any, "male", "female", "both"
  const [isLoading, setIsLoading] = useState(false);
  // Dark mode lazy-init from localStorage so the page is in the right theme
  // from frame 1 (no white flash on Ctrl+R for dark-mode users). The
  // /api/user/settings fetch still runs in the background and overwrites
  // the cache if the server has a newer value.
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try { return localStorage.getItem('darkMode') === 'true'; } catch { return false; }
  });
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineToast, setShowOfflineToast] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceConfig, setMaintenanceConfig] = useState(null);
  const [appSettings, setAppSettings] = useState(() => {
    // Read cached settings synchronously so icon URLs (Explorer, Favorites,
    // Request Word, Word Bank) are present from frame 1. Avoids hide→show on
    // refresh. Cache is updated after every successful settings fetch.
    try {
      const cached = localStorage.getItem('appSettingsCache');
      if (cached) return JSON.parse(cached);
    } catch {}
    return {
      audioGloballyEnabled: true,
      voiceInputEnabled: true,
      autoTranslationEnabled: false,
      favoritesEnabled: true,
      maxFavoritesPerUser: 20,
      feedbackEnabled: true
    };
  });
  
  // My Morocco visibility settings
  // Lazy-init helper: read individual settings cache from localStorage so frame
  // 1 has admin's actual visibility settings (not the hardcoded defaults).
  // Without this, every refresh briefly shows menu items based on defaults,
  // then re-renders when fetch resolves with admin's settings → flicker.
  const _readSettingsCache = (key, fallback) => {
    try { const c = localStorage.getItem(key); if (c) return JSON.parse(c); } catch {}
    return fallback;
  };

  const [travelToolsSettings, setTravelToolsSettings] = useState(() => _readSettingsCache('travelToolsSettingsCache', { is_visible: true, is_pro_only: false }));

  // Music page visibility settings
  const [musicSettings, setMusicSettings] = useState(() => _readSettingsCache('musicSettingsCache', { pageVisible: true, isProOnly: false }));
  const [activeGame, setActiveGame] = useState(null);

  // Culture Pod page visibility settings
  const [culturePodSettings, setCulturePodSettings] = useState(() => _readSettingsCache('culturePodSettingsCache', { pageVisible: true, isProOnly: false }));

  // Decoder page visibility settings
  const [decoderSettings, setDecoderSettings] = useState(() => _readSettingsCache('decoderSettingsCache', { is_visible: true, is_pro_only: false }));

  // Verb Explorer page visibility settings
  const [verbExplorerSettings, setVerbExplorerSettings] = useState(() => _readSettingsCache('verbExplorerSettingsCache', { is_visible: true }));

  // Dictionary page visibility settings
  const [dictionarySettings, setDictionarySettings] = useState(() => _readSettingsCache('dictionarySettingsCache', { is_visible: true, is_pro_only: false }));

  // Amazigh Translation page visibility settings
  const [amazighSettings, setAmazighSettings] = useState(() => _readSettingsCache('amazighSettingsCache', { is_visible: true, is_pro_only: false }));

  // SEO settings (favicon, logo) - fetched from public endpoint
  const [publicSeoSettings, setPublicSeoSettings] = useState(null);
  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${backendUrl}/api/seo/public-settings`)
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setPublicSeoSettings(data); })
      .catch(() => {});
  }, []);

  // URL-based view switching for SEO-friendly URLs.
  // Single source of truth: pathnameToViewState() maps URL → {view, tab?}.
  // Each page's content stays at its own URL — refresh keeps you put.
  useEffect(() => {
    const apply = (pathname) => {
      const state = pathnameToViewState(pathname, window.location.search);
      if (!state) return;
      setCurrentView(state.view);
      // ?tab=<name> in URL takes precedence over the path's default tab.
      // Used by SSR /membership → /profile?tab=membership redirect for logged-in users.
      const urlTab = new URLSearchParams(window.location.search).get('tab');
      if (urlTab) {
        setInitialProfileTab(urlTab);
      } else if (state.tab) {
        setInitialProfileTab(state.tab);
      }
      // Game URL deep-link is handled by GamesSection reading window.location
      // directly — no localStorage middleman.
    };
    apply(window.location.pathname);
    // Listen for popstate (browser back/forward)
    const handlePopState = () => apply(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  
  // Admin authentication state
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [isCheckingAdminAuth, setIsCheckingAdminAuth] = useState(true);
  const [adminToken, setAdminToken] = useState(null); // Initialize as null
  
  // User authentication state
  // Lazy-init from localStorage so frame 1 already knows the user is logged
  // in. Without this, every refresh briefly renders the login form before the
  // useEffect at line ~1057 reads localStorage and updates state.
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(() => {
    try { return !!(localStorage.getItem('user_token') && localStorage.getItem('user_data')); } catch { return false; }
  });
  // Lazy-init from localStorage. Combined with isUserAuthenticated and
  // userToken lazy-init above, this means frame 1 has the COMPLETE auth state
  // populated: token, currentUser, plan. No login-form flash, no avatar flash,
  // no plan-badge flash. Same data the existing auth useEffect would set, just
  // synchronously instead of async.
  const [currentUser, setCurrentUser] = useState(() => {
    try { const u = localStorage.getItem('user_data'); if (u) return JSON.parse(u); } catch {}
    return null;
  });
  const [userToken, setUserToken] = useState(() => {
    try { return localStorage.getItem('user_token') || null; } catch { return null; }
  });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState(
    // Default tab from URL: /signup → register, /login or anything else → login.
    // Used by the existing custom login design (admin-managed, untouched).
    typeof window !== 'undefined' && window.location.pathname === '/signup' ? 'register' : 'login'
  );
  const [formAccessSettings, setFormAccessSettings] = useState(null);
  const [userFeatures, setUserFeatures] = useState(null); // User's plan features
  // userPlan: lazy-init from existing `user_plan` localStorage key so hasPaidPlan
  // is correct from frame 1 on Ctrl+R. Without this, paid pages briefly show the
  // pro-lock overlay then swap to real content. The 'user_plan' key is already
  // written/cleared by various setUserPlan call sites in this file.
  const [userPlan, setUserPlan] = useState(() => {
    try { const c = localStorage.getItem('user_plan'); if (c) return JSON.parse(c); } catch {}
    return null;
  });
  // Lazy-init from localStorage so admin's custom login design (image bg, etc.)
  // shows on frame 1 instead of flashing classic theme then swapping. Same
  // pattern as navDesign cache.
  const [loginDesign, setLoginDesign] = useState(() => {
    try { const c = localStorage.getItem('loginDesignCache'); if (c) return JSON.parse(c); } catch {}
    return { active_design_id: 'classic', custom_designs: [] };
  });
  const [loginDesignLoaded, setLoginDesignLoaded] = useState(() => {
    try { return !!localStorage.getItem('loginDesignCache'); } catch { return false; }
  });
  const [navDesign, setNavDesign] = useState(() => {
    try {
      const c = localStorage.getItem('navDesignCache');
      if (c) return JSON.parse(c);
    } catch {}
    return { active_design_id: 'classic', custom_designs: [], menu_order: [], menu_items: {} };
  });

  // Translator modal states - Lifted up for sticky menu access
  const [isFavoritesOpen, setIsFavoritesOpen] = useState(false);
  const [isProgressOpen, setIsProgressOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [isSuggestPopupOpen, setIsSuggestPopupOpen] = useState(false);
  
  // MEMBERSHIP FIX: Check if user has paid plan — must match components.js UserProfile check
  const hasPaidPlan = userPlan && userPlan.name !== 'free' && userPlan.name !== 'Free Plan';

  // Form access control (hide login/register from landing page)
  const loginHidden = formAccessSettings?.login_form_hidden || false;
  const registerHidden = formAccessSettings?.register_form_hidden || false;
  const hiddenFormMessage = formAccessSettings?.hidden_form_message || '';

  // Translation limit state
  const [translationLimitInfo, setTranslationLimitInfo] = useState({
    canTranslate: true,
    remaining: -1,
    limit: 5,
    used: 0,
    plan: "Guest"
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Load app settings on component mount (with cleanup on unmount)
  useEffect(() => {
    const controller = new AbortController();
    const s = controller.signal;
    loadSettings(s);
    loadMoroccoSettings(s);
    loadMusicSettings(s);
    loadCulturePodSettings(s);
    loadDecoderSettings(s);
    loadDictionarySettings(s);
    loadAmazighSettings(s);
    loadVerbExplorerSettings(s);
    checkTranslationLimit(s);
    fetch(`${API}/website-designs/login`, { signal: s }).then(r => r.json()).then(d => { setLoginDesign(d); setLoginDesignLoaded(true); try { localStorage.setItem('loginDesignCache', JSON.stringify(d)); } catch {} }).catch(e => { if (e.name !== 'AbortError') setLoginDesignLoaded(true); });
    fetch(`${API}/website-designs/navigation`, { signal: s }).then(r => r.json()).then(d => { setNavDesign(d); try { localStorage.setItem('navDesignCache', JSON.stringify(d)); } catch {} }).catch(() => {});
    fetch(`${API}/security/settings/public`, { signal: s }).then(r => r.json()).then(d => setFormAccessSettings(d)).catch(() => {});
    return () => controller.abort();
  }, []);

  // Listen for admin nav design changes to update sidebar live
  useEffect(() => {
    const refetchNav = () => {
      fetch(`${API}/website-designs/navigation`).then(r => r.json()).then(d => { setNavDesign(d); try { localStorage.setItem('navDesignCache', JSON.stringify(d)); } catch {} }).catch(() => {});
    };
    window.addEventListener('navDesignChanged', refetchNav);
    return () => window.removeEventListener('navDesignChanged', refetchNav);
  }, [API]);

  // Listen for admin login design changes to update login page live
  useEffect(() => {
    const refetchLogin = () => {
      fetch(`${API}/website-designs/login`).then(r => r.json()).then(d => { setLoginDesign(d); setLoginDesignLoaded(true); try { localStorage.setItem('loginDesignCache', JSON.stringify(d)); } catch {} }).catch(() => {});
    };
    window.addEventListener('loginDesignChanged', refetchLogin);
    return () => window.removeEventListener('loginDesignChanged', refetchLogin);
  }, [API]);

  // Reload settings when switching views
  useEffect(() => {
    if (currentView === "translator") {
      loadSettings();
      checkTranslationLimit(); // Check translation limit when returning to translator
    }
  }, [currentView]);
  
  // Check translation limit when user logs in/out
  useEffect(() => {
    checkTranslationLimit();
  }, [userToken]);

  // Force authMode when one form is hidden by admin
  useEffect(() => {
    if (loginHidden && !registerHidden) setAuthMode('register');
    if (registerHidden && !loginHidden) setAuthMode('login');
  }, [loginHidden, registerHidden]);

  // Online/Offline detection for PWA
  useEffect(() => {
    const handleOnline = () => {
      console.log('[PWA] Back online');
      setIsOnline(true);
      setShowOfflineToast(false);
    };

    const handleOffline = () => {
      console.log('[PWA] Gone offline');
      setIsOnline(false);
      setShowOfflineToast(true);
      setTimeout(() => setShowOfflineToast(false), 5000);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const loadSettings = async (signal) => {
    try {
      const response = await fetch(`${API}/settings`, { signal });
      const data = await response.json();
      setAppSettings(data);
      try { localStorage.setItem('appSettingsCache', JSON.stringify(data)); } catch {}

      // Check maintenance mode (admins bypass this check)
      if (data.maintenanceMode && !isAdminAuthenticated) {
        setMaintenanceMode(true);
        setMaintenanceConfig(data.maintenanceConfig || {});
      } else {
        setMaintenanceMode(false);
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error("Error loading settings:", error);
    }
  };

  // Bundle loader — fetches ALL public settings in ONE round-trip.
  // Replaces the 9-fetch cascade that caused menu FOUC on page refresh.
  // Fallback: if the bundle endpoint is unavailable, the individual load* functions
  // are still called below as a safety net (they update the same state setters).
  const loadAllSettingsBundle = async (signal) => {
    try {
      const response = await fetch(`${API}/public-settings/bundle`, { signal });
      if (!response.ok) throw new Error(`bundle ${response.status}`);
      const b = await response.json();

      // App settings (translation icons, audio, visibility flags, maintenance mode)
      if (b.app && Object.keys(b.app).length) {
        setAppSettings(b.app);
        if (b.app.maintenanceMode && !isAdminAuthenticated) {
          setMaintenanceMode(true);
          setMaintenanceConfig(b.app.maintenanceConfig || {});
        } else {
          setMaintenanceMode(false);
        }
      }

      if (b.morocco) {
        const v = { is_visible: b.morocco.is_visible !== false, is_pro_only: b.morocco.is_pro_only === true };
        setTravelToolsSettings(v);
        try { localStorage.setItem('travelToolsSettingsCache', JSON.stringify(v)); } catch {}
      }
      if (b.music) {
        const v = { pageVisible: b.music.pageVisible !== false, isProOnly: b.music.isProOnly === true };
        setMusicSettings(v);
        try { localStorage.setItem('musicSettingsCache', JSON.stringify(v)); } catch {}
      }
      if (b.culturePod) {
        const v = { pageVisible: b.culturePod.pageVisible !== false, isProOnly: b.culturePod.isProOnly === true };
        setCulturePodSettings(v);
        try { localStorage.setItem('culturePodSettingsCache', JSON.stringify(v)); } catch {}
      }
      if (b.decoder) {
        const v = { is_visible: b.decoder.is_visible !== false, is_pro_only: b.decoder.is_pro_only === true };
        setDecoderSettings(v);
        try { localStorage.setItem('decoderSettingsCache', JSON.stringify(v)); } catch {}
      }
      if (b.verbExplorer) {
        const v = { is_visible: b.verbExplorer.is_visible !== false };
        setVerbExplorerSettings(v);
        try { localStorage.setItem('verbExplorerSettingsCache', JSON.stringify(v)); } catch {}
      }
      if (b.dictionary) {
        const v = { is_visible: b.dictionary.is_visible !== false, is_pro_only: b.dictionary.is_pro_only === true };
        setDictionarySettings(v);
        try { localStorage.setItem('dictionarySettingsCache', JSON.stringify(v)); } catch {}
      }
      if (b.amazigh) {
        const v = { is_visible: b.amazigh.is_visible !== false, is_pro_only: b.amazigh.is_pro_only === true };
        setAmazighSettings(v);
        try { localStorage.setItem('amazighSettingsCache', JSON.stringify(v)); } catch {}
      }
      if (b.navigation && Object.keys(b.navigation).length) {
        setNavDesign(b.navigation);
      }
      return true;
    } catch (error) {
      if (error.name !== 'AbortError') console.warn("Bundle load failed, falling back to individual fetches:", error.message);
      return false;
    }
  };
  
  // Load My Morocco visibility settings
  const loadMoroccoSettings = async (signal) => {
    try {
      const response = await fetch(`${API}/morocco/settings`, { signal });
      if (response.ok) {
        const data = await response.json();
        const v = { is_visible: data.is_visible !== false, is_pro_only: data.is_pro_only === true };
        setTravelToolsSettings(v);
        try { localStorage.setItem('travelToolsSettingsCache', JSON.stringify(v)); } catch {}
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error("Error loading Morocco settings:", error);
    }
  };

  // Load Culture Pod page visibility settings
  const loadCulturePodSettings = async (signal) => {
    try {
      const response = await fetch(`${API}/culture-pod/settings`, { signal });
      if (response.ok) {
        const data = await response.json();
        const v = { pageVisible: data.pageVisible !== false, isProOnly: data.isProOnly === true };
        setCulturePodSettings(v);
        try { localStorage.setItem('culturePodSettingsCache', JSON.stringify(v)); } catch {}
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error("Error loading Culture Pod settings:", error);
    }
  };

  // Load Music page visibility settings
  const loadMusicSettings = async (signal) => {
    try {
      const response = await fetch(`${API}/music/settings`, { signal });
      if (response.ok) {
        const data = await response.json();
        const v = { pageVisible: data.pageVisible !== false, isProOnly: data.isProOnly === true };
        setMusicSettings(v);
        try { localStorage.setItem('musicSettingsCache', JSON.stringify(v)); } catch {}
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error("Error loading Music settings:", error);
    }
  };

  // Load Decoder page visibility settings
  const loadDecoderSettings = async (signal) => {
    try {
      const response = await fetch(`${API}/decoder/settings`, { signal });
      if (response.ok) {
        const data = await response.json();
        const v = { is_visible: data.is_visible !== false, is_pro_only: data.is_pro_only === true };
        setDecoderSettings(v);
        try { localStorage.setItem('decoderSettingsCache', JSON.stringify(v)); } catch {}
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error("Error loading Decoder settings:", error);
    }
  };

  // Load Verb Explorer page visibility settings
  const loadVerbExplorerSettings = async (signal) => {
    try {
      const response = await fetch(`${API}/verb-explorer/settings`, { signal });
      if (response.ok) {
        const data = await response.json();
        const v = { is_visible: data.is_visible === true };
        setVerbExplorerSettings(v);
        try { localStorage.setItem('verbExplorerSettingsCache', JSON.stringify(v)); } catch {}
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error("Error loading Verb Explorer settings:", error);
    }
  };

  // Load Dictionary page visibility settings
  const loadDictionarySettings = async (signal) => {
    try {
      const response = await fetch(`${API}/dictionary/settings`, { signal });
      if (response.ok) {
        const data = await response.json();
        const v = { is_visible: data.is_visible !== false, is_pro_only: data.is_pro_only === true };
        setDictionarySettings(v);
        try { localStorage.setItem('dictionarySettingsCache', JSON.stringify(v)); } catch {}
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error("Error loading Dictionary settings:", error);
    }
  };

  // Load Amazigh Translation page visibility settings
  const loadAmazighSettings = async (signal) => {
    try {
      const response = await fetch(`${API}/amazigh-translation/settings`, { signal });
      if (response.ok) {
        const data = await response.json();
        const v = { is_visible: data.is_visible !== false, is_pro_only: data.is_pro_only === true };
        setAmazighSettings(v);
        try { localStorage.setItem('amazighSettingsCache', JSON.stringify(v)); } catch {}
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error("Error loading Amazigh settings:", error);
    }
  };

  // Check translation limit
  const checkTranslationLimit = async (signal) => {
    try {
      // Get session ID from localStorage
      let sessionId = localStorage.getItem('darija_session_id');
      if (!sessionId) {
        sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('darija_session_id', sessionId);
      }

      const headers = {
        "X-Session-ID": sessionId
      };

      // Add Authorization header if user is logged in
      if (userToken) {
        headers["Authorization"] = `Bearer ${userToken}`;
      }

      const response = await fetch(`${API}/translation-limit`, { headers, signal });
      if (response.ok) {
        const data = await response.json();
        setTranslationLimitInfo(data);
        return data;
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error("Error checking translation limit:", error);
    }
    return null;
  };

  // Token refresh functionality - automatically refresh token before expiration
  const refreshToken = async () => {
    const token = localStorage.getItem('user_token');
    if (!token) return false;
    
    try {
      console.log('🔄 Attempting to refresh token...');
      const response = await fetch(`${API}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Token refreshed successfully');

        // Update states and localStorage
        setUserToken(data.access_token);
        setCurrentUser(data.user);
        setIsUserAuthenticated(true);
        localStorage.setItem('user_token', data.access_token);
        localStorage.setItem('user_data', JSON.stringify(data.user));

        return true;
      } else {
        // Session-revoked detection: when another device signed in or admin kicked us,
        // verify_user_token returns 401 with { code: 'session_revoked' }. Flag it so the
        // login page can show a clear banner instead of a silent log-out.
        try {
          const data = await response.clone().json();
          if (response.status === 401 && data?.detail?.code === 'session_revoked') {
            sessionStorage.setItem('session_revoked_reason', data.detail.reason || 'Your session ended because another device signed in.');
          }
        } catch (_) { /* ignore */ }
        console.log('❌ Token refresh failed, logging out');
        handleUserLogout();
        return false;
      }
    } catch (error) {
      console.error('❌ Error refreshing token:', error);
      handleUserLogout();
      return false;
    }
  };
  
  // Check token expiration and refresh if needed
  // Token refresh runs every 15 minutes and refreshes token if it's expiring within 30 minutes
  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('user_token');
      if (!token) return;
      
      try {
        // Decode token to check expiration (without verification)
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expirationTime = payload.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const timeUntilExpiry = expirationTime - currentTime;
        
        // Refresh token if it expires in less than 30 minutes (1800000 ms)
        if (timeUntilExpiry < 1800000 && timeUntilExpiry > 0) {
          console.log('⏰ Token expiring soon, refreshing...');
          await refreshToken();
        } else if (timeUntilExpiry <= 0) {
          console.log('⏰ Token expired, logging out');
          handleUserLogout();
        }
      } catch (error) {
        console.error('Error checking token expiration:', error);
      }
    };
    
    // Run immediately
    checkAndRefreshToken();

    // Then check every 15 minutes
    const interval = setInterval(checkAndRefreshToken, 900000); // 15 minutes

    // ALSO check when the user comes back to the tab — catches session-revoked from another device fast
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        const token = localStorage.getItem('user_token');
        if (!token) return;
        // Lightweight session ping — refreshToken will detect session_revoked and route to login banner
        refreshToken();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [userToken, API]);


  // Check for saved admin token on app load
  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      verifyAdminToken(savedToken);
    } else {
      setIsCheckingAdminAuth(false);
    }
  }, []);

  // PWA-safe auth sync: restore state when app resumes from background or another tab changes auth
  useEffect(() => {
    const syncFromLocalStorage = () => {
      const token = localStorage.getItem('user_token');
      const userData = localStorage.getItem('user_data');
      const planData = localStorage.getItem('user_plan');

      if (token && userData && !isUserAuthenticated) {
        try {
          const user = JSON.parse(userData);
          setCurrentUser(user);
          setUserToken(token);
          setIsUserAuthenticated(true);
          if (planData) {
            const plan = JSON.parse(planData);
            setUserPlan(plan);
            if (plan.features) setUserFeatures(plan.features);
          }
        } catch (error) {
          console.error('Auth sync: Error parsing user data:', error);
        }
      } else if (!token && isUserAuthenticated) {
        setCurrentUser(null);
        setUserToken(null);
        setIsUserAuthenticated(false);
        setUserPlan(null);
        setUserFeatures(null);
        try { localStorage.removeItem('user_plan'); } catch {}
      }
    };

    // MEMBERSHIP FIX: When PWA resumes, restore from localStorage immediately, then refresh from server
    // This prevents stale Pro status if subscription expired while app was in background
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        syncFromLocalStorage();
        // Background server refresh to catch subscription changes (expiry, admin cancellation, etc.)
        const token = localStorage.getItem('user_token');
        if (token) {
          try {
            const response = await fetch(`${API}/user/profile`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const profileData = await response.json();
              if (profileData.plan) {
                setUserPlan(profileData.plan);
                if (profileData.plan.features) setUserFeatures(profileData.plan.features);
                localStorage.setItem('user_plan', JSON.stringify(profileData.plan));
              }
            }
          } catch (err) {
            // Silent fail — localStorage data is still valid as fallback
          }
        }
      }
    };

    // Fires when another tab changes localStorage (multi-tab logout)
    const handleStorageChange = (e) => {
      if (e.key === 'user_token' || e.key === 'user_data') syncFromLocalStorage();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isUserAuthenticated, API]);

  // Plan fetching is handled by MEMBERSHIP SYNC (on mount/refresh) and handleUserLogin (on login)
  // Restore auth from localStorage on mount/refresh and fetch fresh profile
  useEffect(() => {
    const savedToken = localStorage.getItem('user_token');
    const savedUser = localStorage.getItem('user_data');

    if (savedToken && savedUser) {
      try {
        const userData = JSON.parse(savedUser);

        // Restore auth states immediately from localStorage
        setCurrentUser(userData);
        setUserToken(savedToken);
        setIsUserAuthenticated(true);
        // Mirror token to cookie so backend SSR routes know we're logged in
        // (covers users who logged in before the cookie-mirror was added)
        if (!document.cookie.split('; ').some((c) => c.startsWith('auth_token='))) {
          document.cookie = `auth_token=${savedToken}; Path=/; Max-Age=${30 * 24 * 60 * 60}; SameSite=Lax`;
        }

        // Also restore cached plan instantly (no flash of "free" state)
        const savedPlan = localStorage.getItem('user_plan');
        if (savedPlan) {
          try {
            const plan = JSON.parse(savedPlan);
            setUserPlan(plan);
            if (plan.features) setUserFeatures(plan.features);
          } catch (e) {}
        }

        // Fetch fresh profile from backend
        (async () => {
          try {
            const response = await fetch(`${API}/user/profile`, {
              headers: { 'Authorization': `Bearer ${savedToken}` }
            });

            if (!response.ok) {
              // Token expired or user deleted — clear auth
              if (response.status === 401 || response.status === 403 || response.status === 404) {
                localStorage.removeItem('user_token');
                localStorage.removeItem('user_data');
                localStorage.removeItem('user_plan');
                setCurrentUser(null);
                setUserToken(null);
                setUserPlan(null);
                setIsUserAuthenticated(false);
                setUserFeatures(null);
              }
              return;
            }

            const profileData = await response.json();

            // Update settings + avatar from DB
            setCurrentUser(prev => {
              if (!prev) return prev;
              const updated = { ...prev };
              if (profileData.settings) updated.settings = profileData.settings;
              if (profileData.avatar !== undefined) updated.avatar = profileData.avatar;
              try {
                const stored = JSON.parse(localStorage.getItem('user_data') || '{}');
                localStorage.setItem('user_data', JSON.stringify({ ...stored, ...(profileData.settings ? { settings: profileData.settings } : {}), ...(profileData.avatar !== undefined ? { avatar: profileData.avatar } : {}) }));
              } catch (e) {}
              return updated;
            });

            // Update plan from DB
            if (profileData.plan) {
              setUserPlan(profileData.plan);
              if (profileData.plan.features) setUserFeatures(profileData.plan.features);
              localStorage.setItem('user_plan', JSON.stringify(profileData.plan));
            }
          } catch (err) {
            console.error('Profile sync error:', err);
          }
        })();

      } catch (error) {
        console.error('Error parsing saved user data:', error);
        localStorage.removeItem('user_token');
        localStorage.removeItem('user_data');
        localStorage.removeItem('user_plan');
        setCurrentUser(null);
        setUserToken(null);
        setUserPlan(null);
        setIsUserAuthenticated(false);
      }
    }
  }, [API]);

  const handleUserLogin = (user, token) => {
    try {
      // Set states immediately
      setCurrentUser(user);
      setUserToken(token);
      setIsUserAuthenticated(true);
      setShowAuthModal(false);
      console.log('🔑 States set, auth modal should close');
      
      // Save to localStorage immediately
      localStorage.setItem('user_token', token);
      localStorage.setItem('user_data', JSON.stringify(user));
      // Mirror token to a cookie so backend SSR routes (/, /membership, /about, etc.)
      // can detect logged-in users and redirect them to the React app instead of
      // showing the anonymous landing page.
      document.cookie = `auth_token=${token}; Path=/; Max-Age=${30 * 24 * 60 * 60}; SameSite=Lax`;
      console.log('🔑 Saved to localStorage + cookie');

      // If user came from SSR Get-Started link, URL has ?plan=<id>.
      // Open the membership tab and highlight the chosen plan so they're
      // one click away from buying.
      try {
        const planFromUrl = new URLSearchParams(window.location.search).get('plan');
        if (planFromUrl) {
          setCurrentView('profile');
          setInitialProfileTab('membership');
          setHighlightPlanId(planFromUrl);
        }
      } catch {}
      
      // Fetch user's plan/subscription - USE ASYNC/AWAIT FOR BETTER CONTROL
      console.log('🔑 About to fetch profile...');
      (async () => {
        try {
          console.log('🔄 Fetching profile...');
          const response = await fetch(`${API}/user/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          console.log('📡 Profile response status:', response.status);
          
          if (!response.ok) {
            console.error('❌ Profile fetch failed:', response.status);
            return;
          }
          
          const profileData = await response.json();
          console.log('📊 Profile Data Received:', profileData);
          
          // Update currentUser with full profile data (includes settings + avatar from DB)
          if (profileData.settings || profileData.avatar !== undefined) {
            setCurrentUser(prev => {
              if (!prev) return prev;
              const next = { ...prev };
              if (profileData.settings) next.settings = profileData.settings;
              if (profileData.avatar !== undefined) next.avatar = profileData.avatar;
              return next;
            });
            const merged = { ...user };
            if (profileData.settings) merged.settings = profileData.settings;
            if (profileData.avatar !== undefined) merged.avatar = profileData.avatar;
            localStorage.setItem('user_data', JSON.stringify(merged));
          }

          if (profileData.plan) {
            console.log('✅ Setting userPlan:', profileData.plan);
            console.log('✅ Plan name:', profileData.plan.name);
            console.log('✅ Is paid plan:', profileData.plan.name !== 'free');

            // Save to localStorage
            localStorage.setItem('user_plan', JSON.stringify(profileData.plan));

            // Update state
            setUserPlan(profileData.plan);

            // Set user features from plan
            if (profileData.plan.features) {
              setUserFeatures(profileData.plan.features);
              console.log('✅ USER FEATURES SET:', profileData.plan.features);
            }

            // Force re-render by also updating a timestamp
            console.log('✅ State updated!');
          } else {
            console.log('❌ No plan in profile data');
          }
        } catch (err) {
          console.error('❌ Error fetching plan:', err);
        }
      })();
      
      console.log('🔑 handleUserLogin END');
    } catch (error) {
      console.error('❌ ERROR in handleUserLogin:', error);
    }
  };

  const handleUserLogout = () => {
    localStorage.removeItem('user_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('user_plan'); // Clear saved plan
    // Clear the auth cookie so backend SSR no longer thinks user is logged in
    document.cookie = 'auth_token=; Path=/; Max-Age=0; SameSite=Lax';
    setCurrentUser(null);
    setUserToken(null); // Set to null, not empty string
    setIsUserAuthenticated(false);
    setUserFeatures(null);
    setUserPlan(null);
  };

  const handleShowAuth = (mode = 'login') => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  // Dark mode - sync from currentUser when it loads, but DON'T reset to light
  // when currentUser is null. The initial useState init already reads the
  // cached preference; clobbering it on every mount before profile loads
  // causes the white flash on Ctrl+R for dark-mode users.
  useEffect(() => {
    if (currentUser?.settings?.darkMode !== undefined) {
      setIsDarkMode(currentUser.settings.darkMode);
    }
    // currentUser === null: keep cached/current value, don't overwrite
  }, [currentUser]);

  // Apply dark mode class to document + persist to localStorage so the next
  // refresh starts in the correct theme without flashing.
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    try { localStorage.setItem('darkMode', String(isDarkMode)); } catch {}
  }, [isDarkMode]);

  // Save dark mode to database when user toggles it (only for logged-in users)
  const saveDarkModeToDatabase = async (darkModeValue) => {
    if (!userToken) return; // Only save if user is logged in
    try {
      const formData = new FormData();
      formData.append('darkMode', darkModeValue);
      await fetch(`${API}/user/settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${userToken}` },
        body: formData
      });
    } catch (error) {
      console.error('Error saving dark mode preference:', error);
    }
  };

  // Load languages on component mount
  useEffect(() => {
    const controller = new AbortController();
    loadLanguages(controller.signal);
    return () => controller.abort();
  }, []);

  const verifyAdminToken = async (token) => {
    try {
      const response = await fetch(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setAdminToken(token);
        setIsAdminAuthenticated(true);
        localStorage.setItem('admin_token', token);
        // Reload settings after admin auth is confirmed to properly bypass maintenance mode
        loadSettings();
      } else {
        localStorage.removeItem('admin_token');
        setIsAdminAuthenticated(false);
      }
    } catch (error) {
      console.error("Token verification failed:", error);
      localStorage.removeItem('admin_token');
      setIsAdminAuthenticated(false);
    } finally {
      setIsCheckingAdminAuth(false);
    }
  };

  const handleAdminLogin = async (username, password) => {
    try {
      const response = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const data = await response.json();
        setAdminToken(data.access_token);
        setIsAdminAuthenticated(true);
        localStorage.setItem('admin_token', data.access_token);
        showNotification("Login successful!", "success");
        return true;
      } else {
        const error = await response.json();
        showNotification(error.detail || "Login failed", "error");
        return false;
      }
    } catch (error) {
      console.error("Login error:", error);
      showNotification("Login failed. Please try again.", "error");
      return false;
    }
  };

  const handleAdminLogout = () => {
    setAdminToken("");
    setIsAdminAuthenticated(false);
    localStorage.removeItem('admin_token');
    setCurrentView("translator");
    showNotification("Logged out successfully", "success");
  };

  const loadLanguages = async (signal) => {
    for (let attempt = 0; attempt < 10; attempt++) {
      try {
        const response = await fetch(`${API}/languages`, { signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setLanguages(data);
        try { localStorage.setItem('languagesCache', JSON.stringify(data)); } catch {}
        const english = data.find(lang => lang.code === "en");
        const chosen = english ? english.code : (data.length > 0 ? data[0].code : "");
        if (chosen) {
          setSelectedLanguage(chosen);
          try { localStorage.setItem('selectedLanguageCache', JSON.stringify(chosen)); } catch {}
        }
        return;
      } catch (e) {
        if (e.name === 'AbortError') return;
        await new Promise(r => setTimeout(r, 2000));
      }
    }
  };

  const showNotification = useCallback((message, type = "info") => {
    // Simple notification system
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 transition-all duration-300 ${
      type === 'error' ? 'bg-red-500 text-white' :
      type === 'success' ? 'bg-green-500 text-white' :
      type === 'warning' ? 'bg-yellow-500 text-black' :
      type === 'info' ? 'bg-blue-500 text-white' :
      'bg-gray-500 text-white'
    }`;
    notification.textContent = message;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.classList.add('opacity-0');
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }, []);

  const handleTranslate = useCallback(async (text, lang) => {
    // Use parameters instead of closure variables
    if (!text || !text.trim() || !lang) {
      return;
    }

    setIsLoading(true);

    // Offline = no translation. Translations always come fresh from backend
    // so admin DB updates take effect immediately for all users — no stale cache.
    if (!isOnline) {
      showNotification("Translation requires internet connection.", "warning");
      setIsLoading(false);
      return;
    }


    try {
      // Get or create session ID for guest users
      let sessionId = localStorage.getItem('darija_session_id');
      if (!sessionId) {
        sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('darija_session_id', sessionId);
      }
      
      // Prepare headers with auth token if available
      const headers = {
        "Content-Type": "application/json",
        "X-Session-ID": sessionId,  // Send session ID in header
      };
      
      if (userToken) {
        headers["Authorization"] = `Bearer ${userToken}`;
      }
      
      const response = await fetch(`${API}/translate`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
          text: text,
          source_language: lang,
          gender_filter: genderFilter || undefined,
        }),
      });

      if (response.status === 429) {
        const errorData = await response.json();
        
        // Check if it's a usage limit error
        if (errorData.detail && typeof errorData.detail === 'object' && errorData.detail.error === 'usage_limit_exceeded') {
          showNotification(errorData.detail.message, "warning");
          setTranslationResult({
            limitExceeded: true,
            message: errorData.detail.message,
            currentPlan: errorData.detail.current_plan,
            limit: errorData.detail.limit,
            used: errorData.detail.used
          });
        } else {
          showNotification("Too many requests. Please wait a moment before trying again.", "warning");
        }
        setIsLoading(false);
        return;
      }

      if (response.status === 404) {
        // Translation not found - show helpful message with request option
        const errorData = await response.json();
        
        // Set a special "not found" result that the UI can handle
        setTranslationResult({
          notFound: true,
          searchedText: text,
          searchedLang: lang,
          message: errorData.detail || "Translation not found in database."
        });
        
        // Don't show popup notification - the UI will show a better message
        setIsLoading(false);
        return;
      }

      // Check for maintenance mode
      if (response.status === 503) {
        const errorData = await response.json();
        if (errorData.detail && errorData.detail.error === 'maintenance_mode') {
          setMaintenanceMode(true);
          setMaintenanceConfig(errorData.detail.config || {});
          setIsLoading(false);
          return;
        }
      }

      if (!response.ok) {
        const errorData = await response.json();
        showNotification(errorData.detail || `Translation failed (${response.status})`, "error");
        setIsLoading(false);
        return;
      }

      const data = await response.json();
      setTranslationResult(data);

      // Refresh translation limit info after successful translation
      checkTranslationLimit();
      
      // Show status indicator only for auto-generated translations
      if (data.status === "auto") {
        showNotification("Translation from API (not verified)", "info");
      }
      // No notification for verified translations - badge is enough
      
    } catch (error) {
      console.error("Error translating text:", error);
      showNotification("Connection error. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [API, showNotification, isOnline, userToken, genderFilter]);

  const handleClearAll = () => {
    setInputText("");
    setTranslationResult(null);
  };

  const toggleDarkMode = () => {
    const newValue = !isDarkMode;
    setIsDarkMode(newValue);
    // Save to database if user is logged in
    if (userToken) {
      saveDarkModeToDatabase(newValue);
    }
  };

  const handleRetryConnection = () => {
    setMaintenanceMode(false);
    loadSettings();
    window.location.reload();
  };

  // Show maintenance page if maintenance mode is active
  // Exceptions:
  // 1. Admin is authenticated (sees admin panel)
  // 2. Admin login route (so admin can login)
  // 3. Special test user "mydarija@yahoo.com" can always access frontend
  const adminRoute = process.env.REACT_APP_ADMIN_ROUTE || 'admin-panel';
  const isAdminRoute = typeof window !== 'undefined' && window.location.pathname === `/${adminRoute}`;
  const isTestUser = currentUser?.email === 'mydarija@yahoo.com';
  
  if (maintenanceMode && !isAdminAuthenticated && !isAdminRoute && !isTestUser) {
    return (
      <Components.MaintenancePage 
        maintenanceConfig={maintenanceConfig}
        onRetry={handleRetryConnection}
        API={API}
        onTestUserLogin={(data) => {
          // Handle test user login from maintenance page
          setCurrentUser(data.user);
          setUserToken(data.token);
          setUserPlan(data.plan || { type: "free", translationsRemaining: 10 });
          localStorage.setItem('user_token', data.token);
          localStorage.setItem('user_data', JSON.stringify(data.user));
          localStorage.setItem('user_plan', JSON.stringify(data.plan || { type: "free", translationsRemaining: 10 }));
          // This will re-render and isTestUser will be true, bypassing maintenance
        }}
      />
    );
  }

  // Admin Maintenance Warning Bar Component - Shows in admin panel when maintenance is ON
  const MaintenanceAdminBar = () => {
    if (!maintenanceMode || !isAdminAuthenticated) return null;
    
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-orange-500 via-red-500 to-orange-500 text-white py-2 px-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-sm font-medium">
          <span className="animate-pulse">🚧</span>
          <span>{t('app_maintenance_active')}</span>
          <span className="hidden sm:inline">—</span>
          <span className="hidden sm:inline">{t('app_users_see_maintenance')}</span>
          <span className="animate-pulse">🚧</span>
        </div>
      </div>
    );
  };

  // Test User Maintenance Warning Bar - Shows to test user when maintenance is ON
  const TestUserMaintenanceBar = () => {
    if (!maintenanceMode || !isTestUser) return null;
    
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 text-white py-2 px-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-sm font-medium">
          <span className="animate-pulse">🔧</span>
          <span>{t('app_test_mode')}</span>
          <span className="hidden sm:inline">—</span>
          <span className="hidden sm:inline">{t('app_maintenance_test_user')}</span>
          <span className="animate-pulse">🔧</span>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      isDarkMode ? 'dark bg-gray-900' : 'bg-gray-50'
    } ${maintenanceMode && (isAdminAuthenticated || isTestUser) ? 'pt-10' : ''}`}>
      {/* Admin Maintenance Warning Bar - Shows only to admin when maintenance mode is ON */}
      <MaintenanceAdminBar />
      {/* Test User Maintenance Warning Bar - Shows to test user when maintenance mode is ON */}
      <TestUserMaintenanceBar />
      <ErrorBoundary>
      <BrowserRouter>
        {/* SEO — inside BrowserRouter so RouteSEO can use useLocation for per-page meta */}
        {publicSeoSettings && (
          <RouteSEO globalSettings={publicSeoSettings} />
        )}
        <PaymentSuccessRefresher API={API} userToken={userToken} setUserPlan={setUserPlan} />
        <Routes>
          {/* Secret Admin Route */}
          <Route path={`/${adminRoute}`} element={
            <div className="min-h-screen bg-gray-50">
              {isCheckingAdminAuth ? (
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center text-gray-800">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-lg">{t('app_loading_admin')}</p>
                  </div>
                </div>
              ) : !isAdminAuthenticated ? (
                <div className="min-h-screen flex items-center justify-center p-4">
                  <div className="max-w-md w-full">
                    <div className="text-center mb-8">
                      <h1 className="text-3xl font-bold mb-2 text-gray-800">
                        🔒 Admin Console
                      </h1>
                      <p className="text-sm text-gray-600">
                        Secure Access Only
                      </p>
                    </div>
                    <AdminLogin
                      onLogin={handleAdminLogin}
                      isDarkMode={false}
                    />
                  </div>
                </div>
              ) : (
                <AdminLayout
                  isDarkMode={false}
                  onLogout={handleAdminLogout}
                >
                  <main className="p-6">
                    <AdminPanel
                      API={API}
                      adminToken={adminToken}
                      languages={languages}
                      onLanguagesUpdate={loadLanguages}
                      onSettingsUpdate={loadSettings}
                      showNotification={showNotification}
                      isDarkMode={false}
                    />
                  </main>
                </AdminLayout>
              )}
            </div>
          } />
          
          {/* Morocco Game Route */}
          <Route path="/morocco-game" element={
            <MoroccoConversationGame />
          } />

          {/* Morocco Journey Game - New Professional Version */}
          <Route path="/morocco-journey" element={
            <MoroccoJourneyGame />
          } />
          
          {/* Morocco Journey Game - Simple Version with Play Button */}
          <Route path="/morocco-journey-simple" element={
            <MoroccoJourneySimple userToken={userToken} />
          } />
          
          {/* Word Puzzle Game - now rendered inside GamesSection */}

          {/* Game Editor Demo - Position Testing Tool */}
          <Route path="/game-editor-old" element={
            <GameEditorDemo />
          } />
          
          {/* Game Editor - PROTECTED ROUTE (Admin Only) */}
          <Route path="/game-editor" element={
            isAdminAuthenticated ? (
              <AdminGameEditor />
            ) : (
              <div className="min-h-screen flex items-center justify-center bg-gray-900">
                <div className="text-center p-8 bg-gray-800 rounded-lg shadow-xl max-w-md">
                  <div className="text-6xl mb-4">🔒</div>
                  <h1 className="text-2xl font-bold text-white mb-4">{t('app_access_denied')}</h1>
                  <p className="text-gray-300 mb-6">
                    This page requires admin authentication.
                  </p>
                  <a 
                    href={`/${adminRoute}`} 
                    className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                  >
                    Go to Admin Login
                  </a>
                </div>
              </div>
            )
          } />

          {/* Game Preview - Protected Route (Admin Only) */}
          <Route path="/game-preview" element={
            isAdminAuthenticated ? (
              <Game isPreviewMode={true} />
            ) : (
              <div className="min-h-screen flex items-center justify-center bg-gray-900">
                <div className="text-center p-8 bg-gray-800 rounded-lg shadow-xl max-w-md">
                  <div className="text-6xl mb-4">🔒</div>
                  <h1 className="text-2xl font-bold text-white mb-4">{t('app_access_denied')}</h1>
                  <p className="text-gray-300 mb-6">
                    Preview requires admin authentication.
                  </p>
                  <a 
                    href={`/${adminRoute}`} 
                    className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                  >
                    Go to Admin Login
                  </a>
                </div>
              </div>
            )
          } />
          
          {/* Static Pages Routes */}
          <Route path="/terms-of-use" element={
            <StaticPage API={API} isDarkMode={isDarkMode} />
          } />
          <Route path="/privacy-policy" element={
            <StaticPage API={API} isDarkMode={isDarkMode} />
          } />
          <Route path="/about-us" element={
            <StaticPage API={API} isDarkMode={isDarkMode} />
          } />
          
          {/* /profile, /membership, /orders, /games, /help, /dashboard, /about,
              /TermsOfUse, /PrivacyPolicy, /help/documentation,
              /help/documentation/deleteaccount fall through to the catch-all so
              their URL stays in the address bar. The pathnameToViewState() helper
              + URL→state useEffect set the right view + tab. */}
          <Route path="/deleteaccount" element={
            <DeleteAccountPage
              API={API}
              userToken={userToken}
              isDarkMode={isDarkMode}
              currentUser={currentUser}
              onLogout={handleUserLogout}
            />
          } />
          <Route path="/reset-password" element={
            <ResetPassword API={API} isDarkMode={isDarkMode} />
          } />
          <Route path="/verify-email" element={
            <VerifyEmail API={API} isDarkMode={isDarkMode} />
          } />
          {/* /dictionary, /amazigh-translation, /my-morocco, /MoroccanHolidays,
              /holidays fall through to catch-all (URL stays put on refresh). */}
          
          {/* Individual Game Routes - SEO Friendly URLs */}
          <Route path="/games/MoroccoJourney" element={
            isUserAuthenticated ? (
              <Game hasPaidPlan={hasPaidPlan} />
            ) : (
              <div className="min-h-screen flex items-center justify-center bg-gray-900">
                <div className="text-center p-8 bg-gray-800 rounded-lg shadow-xl max-w-md">
                  <div className="text-6xl mb-4">🔒</div>
                  <h1 className="text-2xl font-bold text-white mb-4">{t('app_login_required')}</h1>
                  <p className="text-gray-300 mb-6">{t('app_must_login_morocco_journey')}</p>
                  <a href="/" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors">Go to Login</a>
                </div>
              </div>
            )
          } />
          {/* /games/<Name> falls through to catch-all so URL stays put on refresh.
              The pathnameToViewState helper auto-sets games tab + autoOpenGame.
              /games/MoroccoJourney is the exception — it has its own full-screen
              game route above. */}

          {/* Redirect old /game URL to new /games/MoroccoJourney */}
          <Route path="/game" element={
            <GameRedirect setCurrentView={setCurrentView} setInitialProfileTab={setInitialProfileTab} gameId="morocco-journey-redirect" />
          } />
          
          {/* /decoder falls through to catch-all (URL stays put on refresh). */}

          {/* /login and /signup fall through to the catch-all so anonymous
              users see the existing custom-designed loginDesign UI. The
              catch-all already renders that UI when there is no currentUser. */}

          {/* Post-payment landing — Stripe redirects here on success */}
          <Route path="/thank-you" element={
            <PaymentThankYou isDarkMode={isDarkMode} />
          } />

          {/* Account Lockout Pages — full-screen, no app layout */}
          <Route path="/account-banned" element={
            <AccountBanned isDarkMode={isDarkMode} />
          } />
          <Route path="/account-suspended" element={
            <AccountSuspended isDarkMode={isDarkMode} />
          } />

          {/* 404 Page */}
          <Route path="/404" element={
            <NotFoundPage isDarkMode={isDarkMode} />
          } />
          
          {/* Main App Route */}
          <Route path="*" element={
            <>
        <CatchAllGuard />
        {/* Offline Indicator */}
        {!isOnline && (
          <div className="bg-yellow-500 text-white px-4 py-2 text-center text-sm font-medium fixed top-0 left-0 right-0 z-50">
            <svg className="inline-block w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
            </svg>
            You're offline - Using cached data
          </div>
        )}
        
        {/* Offline Toast Notification */}
        {showOfflineToast && (
          <div className="fixed top-20 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-slide-in-right">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="font-medium">{t('app_connection_lost')}</span>
            </div>
          </div>
        )}
        
        {/* Auth Modal */}
        {showAuthModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="relative max-w-md w-full">
              <button
                onClick={() => setShowAuthModal(false)}
                className="absolute -top-10 right-0 text-white hover:text-gray-300"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              {loginHidden && registerHidden ? (
                <div className="text-center py-8 bg-gray-900 rounded-2xl">
                  <div className="text-4xl mb-4">🔒</div>
                  <p className="text-gray-300">{hiddenFormMessage}</p>
                </div>
              ) : (
                <>
                  {!loginHidden && (
                    <div style={{ display: authMode === 'login' ? 'block' : 'none' }}>
                      <Components.UserLogin
                        API={API}
                        onSuccess={handleUserLogin}
                        onSwitchToRegister={() => setAuthMode('register')}
                        isDarkMode={isDarkMode}
                        registerHidden={registerHidden}
                      />
                    </div>
                  )}
                  {!registerHidden && (
                    <div style={{ display: authMode === 'register' ? 'block' : 'none' }}>
                      <Components.UserRegister
                        API={API}
                        onSuccess={handleUserLogin}
                        onSwitchToLogin={() => setAuthMode('login')}
                        isDarkMode={isDarkMode}
                        loginHidden={loginHidden}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        
        {/* Modern Layout with Sidebar */}
        {isAdminAuthenticated ? (
          /* Admin-Only Layout - Always light mode */
          <AdminLayout
            isDarkMode={false}
            onLogout={handleAdminLogout}
          >
            <main className="p-6">
              <AdminPanel
                API={API}
                adminToken={adminToken}
                languages={languages}
                onLanguagesUpdate={loadLanguages}
                onSettingsUpdate={loadSettings}
                showNotification={showNotification}
                isDarkMode={false}
              />
            </main>
          </AdminLayout>
        ) : currentUser ? (
          /* User Layout */
          <ModernLayout
            currentView={currentView}
            setCurrentView={setCurrentView}
            activeProfileTab={initialProfileTab}
            isDarkMode={isDarkMode}
            onToggleDarkMode={toggleDarkMode}
            currentUser={currentUser}
            isAdminAuthenticated={isAdminAuthenticated}
            onLogout={isAdminAuthenticated ? handleAdminLogout : handleUserLogout}
            userPlan={userPlan}
            activeGame={activeGame}
            setInitialProfileTab={setInitialProfileTab}
            hasPaidPlan={hasPaidPlan}
            userToken={userToken}
            showNotification={showNotification}
            onOpenFavorites={() => setIsFavoritesOpen(true)}
            onOpenProgress={() => setIsProgressOpen(true)}
            onOpenCategories={() => setIsCategoriesOpen(true)}
            onOpenSuggest={() => setIsSuggestPopupOpen(true)}
            appSettings={appSettings}
            travelToolsSettings={travelToolsSettings}
            musicSettings={musicSettings}
            culturePodSettings={culturePodSettings}
            decoderSettings={decoderSettings}
            dictionarySettings={dictionarySettings}
            amazighSettings={amazighSettings}
            verbExplorerSettings={verbExplorerSettings}
            navDesign={navDesign}
          >
            <main className={currentView === 'music' || currentView === 'culturePod' ? '' : 'p-6'}>
              {/* App Content Goes Here */}
          {currentView === "translator" && (
            <div className="space-y-6 md:space-y-8">
              <TranslationInterface
                key={`translator-${hasPaidPlan}`}
                languages={languages}
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={(code) => {
                  setSelectedLanguage(code);
                  try { localStorage.setItem('selectedLanguageCache', JSON.stringify(code)); } catch {}
                }}
                inputText={inputText}
                setInputText={setInputText}
                translationResult={translationResult}
                genderFilter={genderFilter}
                setGenderFilter={setGenderFilter}
                isLoading={isLoading}
                onTranslate={handleTranslate}
                onClear={handleClearAll}
                showNotification={showNotification}
                isDarkMode={isDarkMode}
                appSettings={appSettings}
                API={API}
                setCurrentView={setCurrentView}
                setInitialProfileTab={setInitialProfileTab}
                userFeatures={userFeatures}
                userToken={userToken}
                hasPaidPlan={hasPaidPlan}
                translationLimitInfo={translationLimitInfo}
                checkTranslationLimit={checkTranslationLimit}
                isFavoritesOpen={isFavoritesOpen}
                setIsFavoritesOpen={setIsFavoritesOpen}
                isProgressOpen={isProgressOpen}
                setIsProgressOpen={setIsProgressOpen}
                isCategoriesOpen={isCategoriesOpen}
                setIsCategoriesOpen={setIsCategoriesOpen}
                isSuggestPopupOpen={isSuggestPopupOpen}
                setIsSuggestPopupOpen={setIsSuggestPopupOpen}
              />
            </div>
          )}
          
          {currentView === "daily-words" && (
            <Components.ExploreHub
              API={API}
              showNotification={showNotification}
              isDarkMode={isDarkMode}
              appSettings={appSettings}
              userToken={userToken}
              setCurrentView={setCurrentView}
            />
          )}

          {currentView === "decoder" && (
            <DarijaDecoder
              API={API}
              showNotification={showNotification}
              isDarkMode={isDarkMode}
              userToken={userToken}
              currentUser={currentUser}
              hasPaidPlan={hasPaidPlan}
              setCurrentView={setCurrentView}
              setInitialProfileTab={setInitialProfileTab}
            />
          )}

          {currentView === "verbExplorer" && (
            <VerbExplorer
              API={API}
              showNotification={showNotification}
              isDarkMode={isDarkMode}
              userToken={userToken}
              currentUser={currentUser}
              hasPaidPlan={hasPaidPlan}
              setCurrentView={setCurrentView}
              setInitialProfileTab={setInitialProfileTab}
            />
          )}

          {currentView === "membership" && (
            <MembershipPage
              API={API}
              isDarkMode={isDarkMode}
              setCurrentView={setCurrentView}
              setInitialProfileTab={setInitialProfileTab}
              userToken={userToken}
            />
          )}
          
          {currentView === "profile" && currentUser && (
            <UserProfile
              API={API}
              userToken={userToken}
              onLogout={handleUserLogout}
              isDarkMode={isDarkMode}
              onToggleDarkMode={toggleDarkMode}
              isAdmin={false}
              initialTab={initialProfileTab}
              appSettings={appSettings}
              userPlan={userPlan}
              onUserUpdate={(updatedUser) => {
                setCurrentUser(updatedUser);
                localStorage.setItem('user_data', JSON.stringify(updatedUser));
              }}
              setCurrentView={setCurrentView}
              onGameActive={setActiveGame}
              highlightPlanId={highlightPlanId}
              onHighlightDone={() => setHighlightPlanId(null)}
            />
          )}

          {currentView === "profile" && !currentUser && (
            <div className={`text-center py-12 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <p className="mb-4">{t('app_signin_to_profile')}</p>
              <button
                onClick={() => handleShowAuth('login')}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-medium"
              >
                Sign In
              </button>
            </div>
          )}

          {/* Favorites Page */}
          {currentView === "favorites" && (
            <Components.FavoritesPage
              API={API}
              userToken={userToken}
              isDarkMode={isDarkMode}
              showNotification={showNotification}
              setCurrentView={setCurrentView}
            />
          )}

          {/* Dictionary Page */}
          {currentView === "dictionary" && (
            <Dictionary
              API={API}
              isDarkMode={isDarkMode}
              showNotification={showNotification}
              userToken={userToken}
              hasPaidPlan={hasPaidPlan}
              setCurrentView={setCurrentView}
              setInitialProfileTab={setInitialProfileTab}
            />
          )}

          {/* Amazigh Translation Page */}
          {currentView === "amazigh-translation" && (
            <AmazighTranslation
              API={API}
              isDarkMode={isDarkMode}
              showNotification={showNotification}
              userToken={userToken}
              hasPaidPlan={hasPaidPlan}
              setCurrentView={setCurrentView}
              setInitialProfileTab={setInitialProfileTab}
            />
          )}

          {/* My Morocco Page */}
          {currentView === "traveltools" && (
            <TravelTools
              isDarkMode={isDarkMode}
              currentUser={currentUser}
              userToken={userToken}
              hasPaidPlan={hasPaidPlan}
              travelToolsSettings={travelToolsSettings}
              setCurrentView={setCurrentView}
              setInitialProfileTab={setInitialProfileTab}
            />
          )}

          {/* Music Page */}
          {currentView === "music" && (
            <Components.MusicPage
              API={API}
              isDarkMode={isDarkMode}
              showNotification={showNotification}
              currentUser={currentUser}
              userToken={userToken}
              hasPaidPlan={hasPaidPlan}
              setCurrentView={setCurrentView}
              setInitialProfileTab={setInitialProfileTab}
            />
          )}

          {/* Culture Pod Page */}
          {currentView === "culturePod" && (
            <Components.CulturePodPage
              API={API}
              isDarkMode={isDarkMode}
              showNotification={showNotification}
              currentUser={currentUser}
              userToken={userToken}
              hasPaidPlan={hasPaidPlan}
            />
          )}

          {/* Learning Page */}
          {currentView === "learning" && (
            <LearningPage
              API={API}
              isDarkMode={isDarkMode}
              showNotification={showNotification}
              currentUser={currentUser}
              userToken={userToken}
              hasPaidPlan={hasPaidPlan}
              setCurrentView={setCurrentView}
              setInitialProfileTab={setInitialProfileTab}
            />
          )}

          {/* Request Word Page */}
          {currentView === "request" && (
            <Components.RequestWordPage
              API={API}
              userToken={userToken}
              isDarkMode={isDarkMode}
              showNotification={showNotification}
              setCurrentView={setCurrentView}
            />
          )}

          {/* Progress Tracking Page */}
          {currentView === "progress" && (
            <Components.ProgressPage
              API={API}
              userToken={userToken}
              isDarkMode={isDarkMode}
              showNotification={showNotification}
              setCurrentView={setCurrentView}
            />
          )}

          {/* Holidays Page */}
          {currentView === "holidays" && (
            <HolidaysPage
              isDarkMode={isDarkMode}
              currentUser={currentUser}
              userPlan={userPlan}
              onNavigate={setCurrentView}
            />
          )}

          {/* Daily Words Page */}
          {currentView === "my-daily-words" && (
            <DailyWordsPage
              API={API}
              isDarkMode={isDarkMode}
              showNotification={showNotification}
              userToken={userToken}
              hasPaidPlan={hasPaidPlan}
              setCurrentView={setCurrentView}
              setInitialProfileTab={setInitialProfileTab}
            />
          )}

          {/* Dashboard Page */}
          {currentView === "dashboard" && currentUser && (
            <DashboardPage
              API={API}
              isDarkMode={isDarkMode}
              userToken={userToken}
              hasPaidPlan={hasPaidPlan}
              appSettings={appSettings}
              setCurrentView={setCurrentView}
              setInitialProfileTab={setInitialProfileTab}
            />
          )}
        </main>
          </ModernLayout>
        ) : !loginDesignLoaded ? (
          /* Loading - wait for design settings before showing login page */
          <div className="min-h-screen" style={{ background: '#0f172a' }} />
        ) : loginDesign.active_design_id !== 'classic' && (loginDesign.custom_designs || []).find(d => d.id === loginDesign.active_design_id) ? (
          /* Landing Page - Custom Design: Frosted Glass */
          (() => {
            const activeDesign = loginDesign.custom_designs.find(d => d.id === loginDesign.active_design_id);
            const bgStyle = activeDesign.bg_type === 'image' && activeDesign.bg_image_url
              ? { backgroundColor: '#0f172a', backgroundImage: `url(${activeDesign.bg_image_url})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }
              : { background: activeDesign.bg_gradient || 'linear-gradient(135deg, #0f172a, #1e1b4b)' };
            const glassBlur = activeDesign.glass_blur ?? 20;
            const textShadowStyle = activeDesign.text_shadow ? '0 1px 4px rgba(0,0,0,0.5)' : 'none';
            const dc = {
              pageText: activeDesign.page_text_color || '#ffffff',
              featureIcon: activeDesign.feature_icon_color || 'rgba(255,255,255,0.4)',
              footer: activeDesign.footer_color || 'rgba(255,255,255,0.3)',
              langText: activeDesign.lang_text_color || '#9ca3af',
              langActiveBg: activeDesign.lang_active_bg || '#2563eb',
              tabBg: activeDesign.tab_bg || 'rgba(255,255,255,0.08)',
              tabActiveBg: activeDesign.tab_active_bg || 'rgba(255,255,255,0.15)',
              tabActiveText: activeDesign.tab_active_text || '#ffffff',
              tabInactiveText: activeDesign.tab_inactive_text || 'rgba(255,255,255,0.5)',
              cardBg: activeDesign.card_bg && !activeDesign.card_bg.startsWith('rgba')
                ? (() => { const h = activeDesign.card_bg.replace('#',''); return `rgba(${parseInt(h.substring(0,2),16)},${parseInt(h.substring(2,4),16)},${parseInt(h.substring(4,6),16)},${(activeDesign.glass_opacity ?? 10) / 100})`; })()
                : activeDesign.card_bg || `rgba(255,255,255,${(activeDesign.glass_opacity ?? 10) / 100})`,
              cardBorder: activeDesign.card_border || 'rgba(255,255,255,0.15)',
              labelColor: activeDesign.label_color || '#d1d5db',
              iconColor: activeDesign.icon_color || '#6b7280',
              inputBg: activeDesign.input_bg || 'rgba(55,65,81,0.5)',
              inputBorder: activeDesign.input_border || '#4b5563',
              inputText: activeDesign.input_text || '#ffffff',
              inputPlaceholder: activeDesign.input_placeholder || '#6b7280',
              secondaryText: activeDesign.secondary_text_color || '#9ca3af',
              linkColor: activeDesign.link_color || '#2563eb',
              buttonBg: activeDesign.button_bg || '#2563eb',
              buttonText: activeDesign.button_text || '#ffffff',
            };
            return (
          <div className="min-h-screen flex flex-col relative overflow-hidden" style={activeDesign.bg_type !== 'image' ? bgStyle : {}}>
            {activeDesign.bg_type === 'image' && activeDesign.bg_image_url && (
              <div className="absolute inset-0" style={{ ...bgStyle, filter: (activeDesign.image_blur ?? 0) > 0 ? `blur(${activeDesign.image_blur}px)` : 'none', transform: (activeDesign.image_blur ?? 0) > 0 ? 'scale(1.05)' : 'none' }} />
            )}
            <div className="relative flex flex-col-reverse items-center gap-2 md:flex-col-reverse md:items-center md:gap-3 px-4 py-3">
              <a href="/" className="text-sm font-medium hover:underline whitespace-nowrap shrink-0" style={{ color: dc.pageText, opacity: 0.85, textShadow: textShadowStyle }}>← Home</a>
              <LanguageSwitcher isDarkMode={true} size="sm" designColors={{ textColor: dc.langText, activeBg: dc.langActiveBg }} />
            </div>

            <div className="relative flex-1 flex items-center justify-center px-4 pb-8">
              <div className="w-full max-w-sm">
                {activeDesign.welcome_text && (
                  <p className="text-center mb-4 text-lg font-medium" style={{ color: activeDesign.welcome_text_color || '#ffffff', textShadow: textShadowStyle }}>
                    {activeDesign.welcome_text}
                  </p>
                )}

                <div className="text-center mb-6">
                  <img src="/logo.svg" alt="DarijaPro" className="h-16 mx-auto mb-3" />
                  <p className="text-sm" style={{ color: dc.pageText, opacity: 0.6, textShadow: textShadowStyle }}>
                    {t('app_tagline')}
                  </p>
                </div>

                <div className="rounded-2xl p-6" style={{ background: dc.cardBg, backdropFilter: `blur(${glassBlur}px)`, WebkitBackdropFilter: `blur(${glassBlur}px)`, border: `1px solid ${dc.cardBorder}`, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
                  {loginHidden && registerHidden ? (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-4">🔒</div>
                      <p style={{ color: dc.pageText, opacity: 0.8 }}>{hiddenFormMessage}</p>
                    </div>
                  ) : (
                    <>
                      {!loginHidden && !registerHidden && (
                        <div className="flex mb-6 rounded-xl p-1" style={{ background: dc.tabBg }}>
                          <button
                            onClick={() => setAuthMode('login')}
                            className="flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all"
                            style={authMode === 'login'
                              ? { background: dc.tabActiveBg, color: dc.tabActiveText, boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }
                              : { color: dc.tabInactiveText }}
                          >
                            {t('sign_in')}
                          </button>
                          <button
                            onClick={() => setAuthMode('register')}
                            className="flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all"
                            style={authMode === 'register'
                              ? { background: dc.tabActiveBg, color: dc.tabActiveText, boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }
                              : { color: dc.tabInactiveText }}
                          >
                            {t('sign_up')}
                          </button>
                        </div>
                      )}

                      {!loginHidden && (
                        <div style={{ display: authMode === 'login' ? 'block' : 'none' }}>
                          <Components.UserLogin
                            API={API}
                            onSuccess={handleUserLogin}
                            onSwitchToRegister={() => setAuthMode('register')}
                            isDarkMode={true}
                            designColors={dc}
                            registerHidden={registerHidden}
                          />
                        </div>
                      )}
                      {!registerHidden && (
                        <div style={{ display: authMode === 'register' ? 'block' : 'none' }}>
                          <Components.UserRegister
                            API={API}
                            onSuccess={handleUserLogin}
                            onSwitchToLogin={() => setAuthMode('login')}
                            isDarkMode={true}
                            designColors={dc}
                            loginHidden={loginHidden}
                          />
                        </div>
                      )}

                      {(loginHidden || registerHidden) && hiddenFormMessage && (
                        <p className="text-xs text-center mt-3" style={{ color: dc.pageText, opacity: 0.5 }}>{hiddenFormMessage}</p>
                      )}
                    </>
                  )}
                </div>

                <div className="flex items-center justify-center gap-8 mt-8" style={{ color: dc.featureIcon }}>
                  <div className="flex flex-col items-center gap-1.5">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                    </svg>
                    <span className="text-xs">{t('feat_translate')}</span>
                  </div>
                  <div className="flex flex-col items-center gap-1.5">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-xs">{t('feat_games')}</span>
                  </div>
                  <div className="flex flex-col items-center gap-1.5">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    <span className="text-xs">{t('feat_dictionary')}</span>
                  </div>
                  <div className="flex flex-col items-center gap-1.5">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    <span className="text-xs">{t('feat_verb')}</span>
                  </div>
                </div>

                <p className="text-center text-xs mt-6" style={{ color: dc.footer }}>
                  DarijaPro 2026
                </p>
              </div>
            </div>
          </div>
            );
          })()
        ) : (
          /* Landing Page - Design 1: Classic */
          <div className={`min-h-screen flex flex-col ${isDarkMode ? 'bg-gray-950' : 'bg-gradient-to-br from-blue-50 via-white to-orange-50'}`}>
            {/* Top bar: home link (left) + language switcher (center) + dark mode toggle (right).
                On mobile: lang switcher centered on top, Home + dark-mode toggle row below. */}
            <div className="flex flex-col items-center gap-2 md:flex-col md:items-center md:gap-3 px-4 py-3 relative">
              <LanguageSwitcher isDarkMode={isDarkMode} size="sm" />
              <div className="flex items-center gap-6">
                <a href="/" className={`text-sm font-medium hover:underline whitespace-nowrap shrink-0 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>← Home</a>
                <button
                  onClick={toggleDarkMode}
                  className={`p-2 rounded-xl transition-colors shrink-0 ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                  aria-label={t('dark_mode')}
                >
                  {isDarkMode ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Main content - centered */}
            <div className="flex-1 flex items-center justify-center px-4 pb-8">
              <div className="w-full max-w-sm">
                {/* Logo & Branding */}
                <div className="text-center mb-8">
                  <img src="/logo.svg" alt="DarijaPro" className="h-16 mx-auto mb-4" />
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('app_tagline')}
                  </p>
                </div>

                {/* Auth Card */}
                <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-gray-900 border border-gray-800' : 'bg-white shadow-xl shadow-gray-200/50'}`}>
                  {loginHidden && registerHidden ? (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-4">🔒</div>
                      <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>{hiddenFormMessage}</p>
                    </div>
                  ) : (
                    <>
                      {/* Tabs - only show when both forms are available */}
                      {!loginHidden && !registerHidden && (
                        <div className={`flex mb-6 rounded-xl p-1 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                          <button
                            onClick={() => setAuthMode('login')}
                            className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                              authMode === 'login'
                                ? isDarkMode
                                  ? 'bg-gray-700 text-white shadow-sm'
                                  : 'bg-white text-gray-900 shadow-sm'
                                : isDarkMode
                                  ? 'text-gray-400 hover:text-gray-300'
                                  : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            {t('sign_in')}
                          </button>
                          <button
                            onClick={() => setAuthMode('register')}
                            className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                              authMode === 'register'
                                ? isDarkMode
                                  ? 'bg-gray-700 text-white shadow-sm'
                                  : 'bg-white text-gray-900 shadow-sm'
                                : isDarkMode
                                  ? 'text-gray-400 hover:text-gray-300'
                                  : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            {t('sign_up')}
                          </button>
                        </div>
                      )}

                      {/* Auth Forms */}
                      {!loginHidden && (
                        <div style={{ display: authMode === 'login' ? 'block' : 'none' }}>
                          <Components.UserLogin
                            API={API}
                            onSuccess={handleUserLogin}
                            onSwitchToRegister={() => setAuthMode('register')}
                            isDarkMode={isDarkMode}
                            registerHidden={registerHidden}
                          />
                        </div>
                      )}
                      {!registerHidden && (
                        <div style={{ display: authMode === 'register' ? 'block' : 'none' }}>
                          <Components.UserRegister
                            API={API}
                            onSuccess={handleUserLogin}
                            onSwitchToLogin={() => setAuthMode('login')}
                            isDarkMode={isDarkMode}
                            loginHidden={loginHidden}
                          />
                        </div>
                      )}

                      {(loginHidden || registerHidden) && hiddenFormMessage && (
                        <p className={`text-xs text-center mt-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{hiddenFormMessage}</p>
                      )}
                    </>
                  )}
                </div>

                {/* Feature icons */}
                <div className={`flex items-center justify-center gap-8 mt-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <div className="flex flex-col items-center gap-1.5">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                    </svg>
                    <span className="text-xs">{t('feat_translate')}</span>
                  </div>
                  <div className="flex flex-col items-center gap-1.5">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-xs">{t('feat_games')}</span>
                  </div>
                  <div className="flex flex-col items-center gap-1.5">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    <span className="text-xs">{t('feat_dictionary')}</span>
                  </div>
                  <div className="flex flex-col items-center gap-1.5">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    <span className="text-xs">{t('feat_verb')}</span>
                  </div>
                </div>

                {/* Footer */}
                <p className={`text-center text-xs mt-6 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                  DarijaPro 2026
                </p>
              </div>
            </div>
          </div>
        )}
          </>
          } />
        </Routes>
      </BrowserRouter>
      </ErrorBoundary>
      <CookieConsent API={API} isDarkMode={isDarkMode} />
    </div>
  );
}

function AppWithProviders() {
  return (
    <LanguageProvider>
      <App />
    </LanguageProvider>
  );
}

export default AppWithProviders;